import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Copy, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const API_BASE = process.env.REACT_APP_API_URL || "https://analysis.crednx.com";
const APP_BASE = process.env.REACT_APP_APP_URL || "https://app.crednx.com";

const inputCn =
    "w-full rounded-xl border border-border bg-background px-4 py-3 text-[14.5px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

function Field({ label, required, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-foreground">
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
            </label>
            {children}
        </div>
    );
}

// ── Step 1: Signup form ────────────────────────────────────────────────────────
function SignupForm({ onSuccess }) {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", company_name: "", password: "", confirm: "" });
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password !== form.confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/v1/signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name,
                    email: form.email,
                    company_name: form.company_name,
                    password: form.password,
                }),
            });
            if (!res.ok) {
                if (res.status === 409) {
                    setError("An account with this email already exists. Please sign in instead.");
                } else {
                    const data = await res.json().catch(() => ({}));
                    setError(data?.detail || `Signup failed (${res.status}). Please try again.`);
                }
                return;
            }
            onSuccess({ email: form.email, password: form.password });
        } catch {
            setError("Unable to reach the server. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            key="signup-form"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
        >
            <div className="mb-8">
                <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Get started free</p>
                <h1 className="mt-3 font-serif text-[38px] sm:text-[48px] leading-[1.05] tracking-[-0.03em]">
                    Start analysing statements <span className="italic">today.</span>
                </h1>
                <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed">
                    200 statements/month free — no card required.
                </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full name" required>
                        <input
                            value={form.name}
                            onChange={set("name")}
                            required
                            type="text"
                            placeholder="Ayu Wardani"
                            className={inputCn}
                        />
                    </Field>
                    <Field label="Work email" required>
                        <input
                            value={form.email}
                            onChange={set("email")}
                            required
                            type="email"
                            placeholder="you@company.com"
                            className={inputCn}
                        />
                    </Field>
                </div>

                <Field label="Company name" required>
                    <input
                        value={form.company_name}
                        onChange={set("company_name")}
                        required
                        type="text"
                        placeholder="PT Fintech Nusantara"
                        className={inputCn}
                    />
                </Field>

                <Field label="Password" required>
                    <div className="relative">
                        <input
                            value={form.password}
                            onChange={set("password")}
                            required
                            minLength={8}
                            type={showPw ? "text" : "password"}
                            placeholder="Min. 8 characters"
                            className={cn(inputCn, "pr-11")}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </Field>

                <Field label="Confirm password" required>
                    <input
                        value={form.confirm}
                        onChange={set("confirm")}
                        required
                        type="password"
                        placeholder="Repeat password"
                        className={inputCn}
                    />
                </Field>

                {error && (
                    <p className="text-[13.5px] text-destructive rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
                        {error}
                    </p>
                )}

                <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                >
                    {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</>
                    ) : (
                        <>Create free account <ArrowUpRight className="w-4 h-4 ml-1" /></>
                    )}
                </Button>

                <p className="text-center text-[13px] text-muted-foreground">
                    Already have an account?{" "}
                    <a
                        href={`${APP_BASE}/login`}
                        className="text-primary font-medium hover:underline"
                    >
                        Sign in
                    </a>
                </p>
                <p className="text-center text-[12px] text-muted-foreground">
                    By signing up you agree to our{" "}
                    <Link to="/terms" className="underline underline-offset-2">Terms</Link>
                    {" & "}
                    <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
                </p>
            </form>
        </motion.div>
    );
}

// ── Step 2: OTP verification ───────────────────────────────────────────────────
function OTPStep({ email, password, onSuccess }) {
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [resent, setResent] = useState(false);

    const verify = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/v1/verify-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data?.detail || "Invalid code. Please try again.");
                return;
            }
            const authKey = data?.result?.auth_key || data?.auth_key || "";
            // Try auto-login to get JWT and redirect straight into the dashboard
            try {
                const loginRes = await fetch(`${API_BASE}/auth/v1/login`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email, password }),
                });
                const loginData = await loginRes.json();
                const inner = loginData?.result?.json || loginData?.result || loginData;
                if (loginRes.ok && inner.token) {
                    const userParam = encodeURIComponent(JSON.stringify(inner.user || {}));
                    window.location.href = `${APP_BASE}?token=${encodeURIComponent(inner.token)}&auth_key=${encodeURIComponent(authKey)}&user=${userParam}`;
                    return;
                }
            } catch {}
            // Fallback: show API key success screen
            onSuccess({ authKey });
        } catch {
            setError("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        try {
            await fetch(`${API_BASE}/auth/v1/resend-verification`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            setResent(true);
            setTimeout(() => setResent(false), 5000);
        } catch {}
    };

    return (
        <motion.div
            key="otp-step"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
        >
            <div className="mb-8">
                <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Verify your email</p>
                <h1 className="mt-3 font-serif text-[38px] sm:text-[48px] leading-[1.05] tracking-[-0.03em]">
                    Check your <span className="italic">inbox.</span>
                </h1>
                <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed">
                    We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>.
                    Enter it below to activate your account.
                </p>
            </div>

            <form onSubmit={verify} className="space-y-5">
                <Field label="Verification code" required>
                    <input
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        required
                        type="text"
                        inputMode="numeric"
                        placeholder="123456"
                        maxLength={6}
                        className={cn(inputCn, "text-center text-[24px] font-mono tracking-[0.3em]")}
                    />
                </Field>

                {error && (
                    <p className="text-[13.5px] text-destructive rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
                        {error}
                    </p>
                )}

                <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={loading || otp.length < 6}
                >
                    {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
                    ) : (
                        "Verify & activate account"
                    )}
                </Button>

                <p className="text-center text-[13px] text-muted-foreground">
                    Didn't get it?{" "}
                    <button
                        type="button"
                        onClick={resend}
                        className="text-primary font-medium hover:underline"
                    >
                        Resend code
                    </button>
                    {resent && <span className="ml-2 text-emerald-600">Sent!</span>}
                </p>
            </form>
        </motion.div>
    );
}

// ── Step 3: Success — show API key ─────────────────────────────────────────────
function SuccessStep({ authKey }) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(authKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    return (
        <motion.div
            key="success-step"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center"
        >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>

            <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">You're in</p>
            <h1 className="mt-3 font-serif text-[38px] sm:text-[48px] leading-[1.05] tracking-[-0.03em]">
                Account <span className="italic">activated.</span>
            </h1>
            <p className="mt-4 text-[15px] text-muted-foreground">
                Here is your API key. Keep it safe — it won't be shown again.
            </p>

            <div className="mt-8 rounded-2xl border border-border bg-muted/40 p-5 text-left">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Your API Key</p>
                <div className="flex items-center gap-3">
                    <code className="flex-1 font-mono text-[13px] break-all text-foreground">
                        {authKey}
                    </code>
                    <button
                        onClick={copy}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-[12.5px] font-medium hover:bg-muted transition-colors"
                    >
                        <Copy className="w-3.5 h-3.5" />
                        {copied ? "Copied!" : "Copy"}
                    </button>
                </div>
            </div>

            <div className="mt-6 rounded-xl bg-primary/5 border border-primary/10 px-5 py-4 text-[13px] text-muted-foreground text-left">
                Use it as the <code className="font-mono text-foreground">X-Auth-Key</code> header on every API request.
                Your Starter plan includes <strong className="text-foreground">200 statements/month</strong> free.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="lg" className="flex-1" asChild>
                    <a href={APP_BASE}>
                        Go to dashboard <ArrowUpRight className="w-4 h-4 ml-1" />
                    </a>
                </Button>
                <Button variant="outline" size="lg" className="flex-1" asChild>
                    <Link to="/products/bsa">View API docs</Link>
                </Button>
            </div>
        </motion.div>
    );
}

// ── Page shell ────────────────────────────────────────────────────────────────
export default function Signup() {
    const [step, setStep] = useState("form"); // "form" | "otp" | "success"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [authKey, setAuthKey] = useState("");

    return (
        <div data-testid="page-signup">
            <section className="relative overflow-hidden min-h-[calc(100vh-72px)] flex items-center">
                <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
                <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-55 pointer-events-none" />

                <div className="relative container max-w-[1240px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left: trust strip (visible on large screens) */}
                    <div className="hidden lg:block">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Trusted by lenders across SEA</p>
                        <div className="mt-6 space-y-4">
                            {[
                                { stat: "220+", label: "Banks supported across SEA" },
                                { stat: "5", label: "Markets — ID, PH, VN, MY, SG" },
                                { stat: "<3s", label: "Average extraction time" },
                                { stat: "200", label: "Free statements/month on Starter" },
                            ].map(({ stat, label }) => (
                                <div key={stat} className="flex items-center gap-4">
                                    <span className="font-serif text-[32px] tracking-tight text-primary w-20 shrink-0">{stat}</span>
                                    <span className="text-[14px] text-muted-foreground">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: form card */}
                    <div className="rounded-3xl border border-border bg-card p-8 md:p-10 shadow-elegant">
                        <AnimatePresence mode="wait">
                            {step === "form" && (
                                <SignupForm
                                    key="form"
                                    onSuccess={({ email: e, password: p }) => {
                                        setEmail(e);
                                        setPassword(p);
                                        setStep("otp");
                                    }}
                                />
                            )}
                            {step === "otp" && (
                                <OTPStep
                                    key="otp"
                                    email={email}
                                    password={password}
                                    onSuccess={({ authKey: k }) => {
                                        setAuthKey(k);
                                        setStep("success");
                                    }}
                                />
                            )}
                            {step === "success" && (
                                <SuccessStep key="success" authKey={authKey} />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </section>
        </div>
    );
}
